import React, { useState, useEffect, useContext, useCallback, useRef } from 'react';
import { AuthContext } from '../context/AuthContext';
import { FaServer, FaExclamationTriangle, FaShieldAlt, FaSun, FaMoon, FaPlay, FaStop, FaTrash, FaDownload } from 'react-icons/fa';
import { MdSpeed, MdError, MdCheckCircle, MdWarning, MdTrendingUp, MdHistory } from 'react-icons/md';
import { Line, Bar, Doughnut } from 'react-chartjs-2';
import { io } from 'socket.io-client';
import './AdminMonitoring.css';

// Chart.js configuration
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

const API_BASE = process.env.REACT_APP_API_BASE_URL || 'https://intranet.nesimltd.co.ke';

export default function AdminMonitoring() {
  const { user, token } = useContext(AuthContext);
  const [loading, setLoading] = useState(true);
  const [overview, setOverview] = useState(null);
  const [errors, setErrors] = useState(null);
  const [performance, setPerformance] = useState(null);

  const [rateLimits, setRateLimits] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [lastUpdated, setLastUpdated] = useState(null);
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [isConnected, setIsConnected] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState('disconnected');
  const [alerts, setAlerts] = useState([]);
  
  // Historical data states
  const [historicalData, setHistoricalData] = useState(null);
  const [healthTrends, setHealthTrends] = useState(null);
  const [performanceTrends, setPerformanceTrends] = useState(null);
  const [errorTrends, setErrorTrends] = useState(null);
  const [collectionStatus, setCollectionStatus] = useState({ isCollecting: false, lastCollection: null });
  const [timeRange, setTimeRange] = useState('7d'); // 1d, 7d, 30d, 90d
  const [aggregationInterval, setAggregationInterval] = useState('hour'); // minute, hour, day
  
  // Error logs states
  const [errorLogs, setErrorLogs] = useState([]);
  const [errorLogsStats, setErrorLogsStats] = useState(null);
  const [errorLogsTrends, setErrorLogsTrends] = useState(null);
  const [errorLogsFilters, setErrorLogsFilters] = useState({
    level: 'all',
    category: 'all',
    severity: 'all',
    status: 'all',
    module: 'all',
    startDate: '',
    endDate: '',
    limit: 100,
    page: 1
  });
  const [errorLogsPagination, setErrorLogsPagination] = useState({
    page: 1,
    limit: 100,
    total: 0,
    pages: 0
  });
  
  // User activity states
  const [userActivities, setUserActivities] = useState([]);
  const [userActivityStats, setUserActivityStats] = useState(null);
  const [userActivityTrends, setUserActivityTrends] = useState(null);
  const [frequentlyVisitedPages, setFrequentlyVisitedPages] = useState([]);
  const [topActiveUsers, setTopActiveUsers] = useState([]);
  const [pageAnalytics, setPageAnalytics] = useState([]);
  const [userActivityFilters, setUserActivityFilters] = useState({
    userId: '',
    userEmail: '',
    userRole: 'all',
    page: '',
    route: '',
    method: 'all',
    action: 'all',
    deviceType: 'all',
    browser: 'all',
    os: 'all',
    hasError: 'all',
    startDate: '',
    endDate: '',
    limit: 100,
    page: 1
  });
  const [userActivityPagination, setUserActivityPagination] = useState({
    page: 1,
    limit: 100,
    total: 0,
    pages: 0
  });
  
  const socketRef = useRef(null);
  const updateIntervalRef = useRef(null);

  // Detect system theme and colors
  useEffect(() => {
    const updateSystemColors = (prefersDark) => {
      const root = document.documentElement;
      setIsDarkMode(prefersDark);
      
      if (prefersDark) {
        root.style.setProperty('--system-accent-color', '#60c5fa');
        root.style.setProperty('--system-accent-color-dark', '#0078d4');
        root.style.setProperty('--system-background', '#1f1f1f');
        root.style.setProperty('--system-surface', 'rgba(32, 32, 32, 0.95)');
        root.style.setProperty('--system-text-primary', '#ffffff');
        root.style.setProperty('--system-text-secondary', '#cccccc');
        root.style.setProperty('--system-border', 'rgba(255, 255, 255, 0.1)');
        root.style.setProperty('--system-shadow', 'rgba(0, 0, 0, 0.3)');
      } else {
        root.style.setProperty('--system-accent-color', '#0078d4');
        root.style.setProperty('--system-accent-color-dark', '#106ebe');
        root.style.setProperty('--system-background', '#ffffff');
        root.style.setProperty('--system-surface', 'rgba(255, 255, 255, 0.95)');
        root.style.setProperty('--system-text-primary', '#323130');
        root.style.setProperty('--system-text-secondary', '#605e5c');
        root.style.setProperty('--system-border', 'rgba(255, 255, 255, 0.2)');
        root.style.setProperty('--system-shadow', 'rgba(0, 0, 0, 0.1)');
      }
    };

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    updateSystemColors(mediaQuery.matches);
    mediaQuery.addEventListener('change', (e) => updateSystemColors(e.matches));
    
    return () => {
      mediaQuery.removeEventListener('change', (e) => updateSystemColors(e.matches));
    };
  }, []);

  const toggleTheme = () => {
    const newMode = !isDarkMode;
    setIsDarkMode(newMode);
    
    const root = document.documentElement;
    if (newMode) {
      root.style.setProperty('--system-accent-color', '#60c5fa');
      root.style.setProperty('--system-accent-color-dark', '#0078d4');
      root.style.setProperty('--system-background', '#1f1f1f');
      root.style.setProperty('--system-surface', 'rgba(32, 32, 32, 0.95)');
      root.style.setProperty('--system-text-primary', '#ffffff');
      root.style.setProperty('--system-text-secondary', '#cccccc');
      root.style.setProperty('--system-border', 'rgba(255, 255, 255, 0.1)');
      root.style.setProperty('--system-shadow', 'rgba(0, 0, 0, 0.3)');
    } else {
      root.style.setProperty('--system-accent-color', '#0078d4');
      root.style.setProperty('--system-accent-color-dark', '#106ebe');
      root.style.setProperty('--system-background', '#ffffff');
      root.style.setProperty('--system-surface', 'rgba(255, 255, 255, 0.95)');
      root.style.setProperty('--system-text-primary', '#323130');
      root.style.setProperty('--system-text-secondary', '#605e5c');
      root.style.setProperty('--system-border', 'rgba(255, 255, 255, 0.2)');
      root.style.setProperty('--system-shadow', 'rgba(0, 0, 0, 0.1)');
    }
  };

  const fetchMonitoringData = useCallback(async () => {
    try {
      setLoading(true);
      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      };

      // Fetch all monitoring data in parallel
      const [overviewRes, errorsRes, performanceRes, rateLimitsRes] = await Promise.all([
        fetch(`${API_BASE}/api/admin/monitoring/overview`, { headers }),
        fetch(`${API_BASE}/api/admin/monitoring/errors`, { headers }),
        fetch(`${API_BASE}/api/admin/monitoring/performance`, { headers }),
        fetch(`${API_BASE}/api/admin/monitoring/rate-limits`, { headers })
      ]);

      if (overviewRes.ok) setOverview(await overviewRes.json());
      if (errorsRes.ok) setErrors(await errorsRes.json());
      if (performanceRes.ok) setPerformance(await performanceRes.json());

      if (rateLimitsRes.ok) setRateLimits(await rateLimitsRes.json());

      setLastUpdated(new Date());
    } catch (error) {
      console.error('Error fetching monitoring data:', error);
      // Show sample data when backend is not available
      setOverview({
        systemHealth: { status: 'healthy', errorRate: '0.00%', dailyErrorRate: '0.00%', threshold: '1%' },
        performance: { avgResponseTime: '45ms', p95ResponseTime: '120ms', p99ResponseTime: '200ms', targetResponseTime: '150ms' },
        system: { uptime: 3600, memoryUsage: { heapUsed: 52428800 }, nodeVersion: 'v22.17.0', environment: 'development' }
      });
      setErrors({
        totalRequests: 1250,
        totalErrors: 3,
        currentErrorRate: '0.24%',
        recommendations: ['System is performing well', 'No immediate action required'],
        errorCounts: { 'GET /api/users': 1, 'POST /api/auth/login': 2 }
      });
      setPerformance({
        responseTimes: { average: 45, p95: 120, p99: 200 },
        throughput: { requestsPerSecond: 25, activeConnections: 15 },
        database: { avgQueryTime: 12, slowQueries: 0 }
      });

      setRateLimits({
        http: { perUser: { current: 45, limit: 100 }, perIP: { current: 120, limit: 200 } },
        websocket: { perUser: { current: 10, limit: 50 } },
        blockedRequests: { total: 0, lastHour: 0 }
      });
      setLastUpdated(new Date());
    } finally {
      setLoading(false);
    }
  }, [token]);

  // Initialize WebSocket connection for real-time updates
  useEffect(() => {
    if (token && user) {
      // Connect to WebSocket for real-time monitoring updates
      socketRef.current = io('/', {
        path: '/socket.io',
        transports: ['websocket'],
        withCredentials: true,
        // The server verifies this JWT in the handshake; read it on every (re)connect
        auth: cb => cb({ token: localStorage.getItem('token') }),
      });

      socketRef.current.on('connect', () => {
        console.log('🔌 Monitoring WebSocket connected');
        setIsConnected(true);
        setConnectionStatus('connected');
        
        // Join monitoring room
        socketRef.current.emit('join-monitoring', { userId: user.id, role: user.role });
      });

      socketRef.current.on('disconnect', () => {
        console.log('🔌 Monitoring WebSocket disconnected');
        setIsConnected(false);
        setConnectionStatus('disconnected');
      });

      socketRef.current.on('monitoring-update', (data) => {
        console.log('📊 Received real-time monitoring update:', data);
        
        // Update specific data based on type
        switch (data.type) {
          case 'overview':
            setOverview(data.data);
            break;
          case 'errors':
            setErrors(data.data);
            break;
          case 'performance':
            setPerformance(data.data);
            break;

          case 'rate-limits':
            setRateLimits(data.data);
            break;
          default:
            // Full refresh if type is unknown
            fetchMonitoringData();
        }
        
        setLastUpdated(new Date());
      });

      socketRef.current.on('error', (error) => {
        console.error('❌ Monitoring WebSocket error:', error);
        setConnectionStatus('error');
      });

      return () => {
        if (socketRef.current) {
          socketRef.current.disconnect();
        }
      };
    }
  }, [token, user]);

  // Set up automatic refresh interval
  useEffect(() => {
    if (token && user) {
      // Initial fetch
      fetchMonitoringData();
      
      // Set up interval for fallback updates (every 30 seconds)
      updateIntervalRef.current = setInterval(fetchMonitoringData, 30000);
      
      return () => {
        if (updateIntervalRef.current) {
          clearInterval(updateIntervalRef.current);
        }
      };
    }
  }, [token, user, fetchMonitoringData]);

  // Check for critical alerts
  const checkForAlerts = useCallback(() => {
    const newAlerts = [];
    
    // Check error rate alerts
    if (errors?.currentErrorRate && parseFloat(errors.currentErrorRate) >= 1) {
      newAlerts.push({
        id: 'error-rate-high',
        type: 'critical',
        title: 'High Error Rate',
        message: `Error rate is ${errors.currentErrorRate}, exceeding 1% threshold`,
        timestamp: new Date().toISOString()
      });
    }
    
    // Check performance alerts
    if (performance?.responseTimes?.p95 && performance.responseTimes.p95 > 150) {
      newAlerts.push({
        id: 'response-time-slow',
        type: 'warning',
        title: 'Slow Response Times',
        message: `95th percentile response time is ${performance.responseTimes.p95}ms, above 150ms target`,
        timestamp: new Date().toISOString()
      });
    }
    
    // Check memory alerts
    if (overview?.system?.memoryUsage?.heapUsed && overview.system.memoryUsage.heapUsed > 100) {
      newAlerts.push({
        id: 'memory-usage-high',
        type: 'warning',
        title: 'High Memory Usage',
        message: `Memory usage is ${overview.system.memoryUsage.heapUsed}MB, consider monitoring`,
        timestamp: new Date().toISOString()
      });
    }
    
    setAlerts(newAlerts);
  }, [errors, performance, overview]);

  // Update alerts when data changes
  useEffect(() => {
    checkForAlerts();
  }, [checkForAlerts]);



  // Export monitoring data
  const exportData = useCallback((format = 'json') => {
    const data = {
      overview,
      errors,
      performance,
      rateLimits,
      exportTimestamp: new Date().toISOString(),
      exportUser: user?.email
    };

    if (format === 'json') {
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `monitoring-data-${new Date().toISOString().split('T')[0]}.json`;
      a.click();
      URL.revokeObjectURL(url);
    } else if (format === 'csv') {
      // Convert to CSV format
      const csvContent = convertToCSV(data);
      const blob = new Blob([csvContent], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `monitoring-data-${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    }
  }, [overview, errors, performance, rateLimits, user]);

  // Convert data to CSV format
  const convertToCSV = (data) => {
    const flattenObject = (obj, prefix = '') => {
      return Object.keys(obj).reduce((acc, key) => {
        const pre = prefix.length ? prefix + '.' : '';
        if (typeof obj[key] === 'object' && obj[key] !== null && !Array.isArray(obj[key])) {
          Object.assign(acc, flattenObject(obj[key], pre + key));
        } else {
          acc[pre + key] = obj[key];
        }
        return acc;
      }, {});
    };

    const flattened = flattenObject(data);
    const headers = Object.keys(flattened);
    const csvRows = [headers.join(',')];
    
    const values = headers.map(header => {
      const value = flattened[header];
      return typeof value === 'string' ? `"${value.replace(/"/g, '""')}"` : value;
    });
    csvRows.push(values.join(','));
    
    return csvRows.join('\n');
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'healthy': return <MdCheckCircle className="status-icon healthy" />;
      case 'degraded': return <MdWarning className="status-icon warning" />;
      case 'error': return <MdError className="status-icon error" />;
      default: return <MdCheckCircle className="status-icon" />;
    }
  };

  // Helper function to format uptime
  const formatUptime = (seconds) => {
    if (!seconds) return '0m';
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    }
    return `${minutes}m`;
  };

  const formatBytes = (bytes) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // Fetch historical data
  const fetchHistoricalData = useCallback(async () => {
    try {
      const days = timeRange === '1d' ? 1 : timeRange === '7d' ? 7 : timeRange === '30d' ? 30 : 90;
      
      const [historicalRes, healthRes, performanceRes, errorRes] = await Promise.all([
        fetch(`${API_BASE}/api/admin/monitoring/historical?startDate=${new Date(Date.now() - days * 24 * 60 * 60 * 1000).toISOString()}&interval=${aggregationInterval}`, {
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
        }),
        fetch(`${API_BASE}/api/admin/monitoring/trends/health?days=${days}`, {
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
        }),
        fetch(`${API_BASE}/api/admin/monitoring/trends/performance?days=${days}`, {
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
        }),
        fetch(`${API_BASE}/api/admin/monitoring/trends/errors?days=${days}`, {
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
        })
      ]);

      if (historicalRes.ok) setHistoricalData(await historicalRes.json());
      if (healthRes.ok) setHealthTrends(await healthRes.json());
      if (performanceRes.ok) setPerformanceTrends(await performanceRes.json());
      if (errorRes.ok) setErrorTrends(await errorRes.json());

    } catch (error) {
      console.error('Error fetching historical data:', error);
    }
  }, [token, timeRange, aggregationInterval]);

  // Fetch collection status
  const fetchCollectionStatus = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE}/api/admin/monitoring/collection/status`, {
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
      });
      
      if (response.ok) {
        const status = await response.json();
        setCollectionStatus(status);
      }
    } catch (error) {
      console.error('Error fetching collection status:', error);
    }
  }, [token]);

  // Start data collection
  const startDataCollection = async () => {
    try {
      const response = await fetch(`${API_BASE}/api/admin/monitoring/collection/start`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ intervalMinutes: 1 })
      });
      
      if (response.ok) {
        await fetchCollectionStatus();
        console.log('Data collection started');
      }
    } catch (error) {
      console.error('Error starting data collection:', error);
    }
  };

  // Stop data collection
  const stopDataCollection = async () => {
    try {
      const response = await fetch(`${API_BASE}/api/admin/monitoring/collection/stop`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
      });
      
      if (response.ok) {
        await fetchCollectionStatus();
        console.log('Data collection stopped');
      }
    } catch (error) {
      console.error('Error stopping data collection:', error);
    }
  };

  // Manual data collection
  const collectDataManually = async () => {
    try {
      const response = await fetch(`${API_BASE}/api/admin/monitoring/collect`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
      });
      
      if (response.ok) {
        await fetchCollectionStatus();
        await fetchHistoricalData();
        console.log('Manual data collection completed');
      }
    } catch (error) {
      console.error('Error collecting data manually:', error);
    }
  };

  // Fetch error logs
  const fetchErrorLogs = useCallback(async () => {
    try {
      const queryParams = new URLSearchParams({
        level: errorLogsFilters.level,
        category: errorLogsFilters.category,
        severity: errorLogsFilters.severity,
        status: errorLogsFilters.status,
        module: errorLogsFilters.module,
        limit: errorLogsFilters.limit.toString(),
        page: errorLogsFilters.page.toString()
      });

      if (errorLogsFilters.startDate) {
        queryParams.append('startDate', errorLogsFilters.startDate);
      }
      if (errorLogsFilters.endDate) {
        queryParams.append('endDate', errorLogsFilters.endDate);
      }

      const response = await fetch(`${API_BASE}/api/admin/error-logs?${queryParams}`, {
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
      });
      
      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          setErrorLogs(result.data);
          setErrorLogsPagination(result.pagination);
        }
      }
    } catch (error) {
      console.error('Error fetching error logs:', error);
    }
  }, [token, errorLogsFilters]);

  // Fetch error logs statistics
  const fetchErrorLogsStats = useCallback(async () => {
    try {
      const queryParams = new URLSearchParams();
      if (errorLogsFilters.startDate) {
        queryParams.append('startDate', errorLogsFilters.startDate);
      }
      if (errorLogsFilters.endDate) {
        queryParams.append('endDate', errorLogsFilters.endDate);
      }

      const response = await fetch(`${API_BASE}/api/admin/error-logs/stats?${queryParams}`, {
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
      });
      
      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          setErrorLogsStats(result.data);
        }
      }
    } catch (error) {
      console.error('Error fetching error logs statistics:', error);
    }
  }, [token, errorLogsFilters.startDate, errorLogsFilters.endDate]);

  // Fetch error logs trends
  const fetchErrorLogsTrends = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE}/api/admin/error-logs/trends?days=7&interval=hour`, {
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
      });
      
      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          setErrorLogsTrends(result.data);
        }
      }
    } catch (error) {
      console.error('Error fetching error logs trends:', error);
    }
  }, [token]);

  // Cleanup old error logs
  const cleanupErrorLogs = async () => {
    try {
      const response = await fetch(`${API_BASE}/api/admin/error-logs/cleanup`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ daysToKeep: 365 })
      });
      
      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          console.log(`Cleaned up ${result.deletedCount} old error logs`);
          await fetchErrorLogs();
          await fetchErrorLogsStats();
        }
      }
    } catch (error) {
      console.error('Error cleaning up error logs:', error);
    }
  };

  // Test error logging
  const testErrorLogging = async () => {
    try {
      const response = await fetch(`${API_BASE}/api/admin/error-logs/test`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          type: 'error',
          message: 'Test error message from monitoring dashboard',
          category: 'test'
        })
      });
      
      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          console.log('Test error logged successfully');
          await fetchErrorLogs();
          await fetchErrorLogsStats();
        }
      }
    } catch (error) {
      console.error('Error testing error logging:', error);
    }
  };

  // View error log details
  const viewErrorLogDetails = (log) => {
    // This would open a modal with detailed error information
    console.log('Viewing error log details:', log);
    alert(`Error Log Details:\n\nMessage: ${log.message}\nType: ${log.errorType}\nCategory: ${log.category}\nSeverity: ${log.severity}\nModule: ${log.context?.module}\nTimestamp: ${new Date(log.timestamp).toLocaleString()}\n\nStack Trace:\n${log.stack || 'No stack trace available'}`);
  };

  // Edit error log
  const editErrorLog = (log) => {
    // This would open a modal for editing resolution status
    console.log('Editing error log:', log);
    const newStatus = prompt('Enter new status (open, investigating, resolved, closed):', log.resolution?.status || 'open');
    if (newStatus && ['open', 'investigating', 'resolved', 'closed'].includes(newStatus)) {
      updateErrorLogStatus(log._id, newStatus);
    }
  };

  // Update error log status
  const updateErrorLogStatus = async (logId, status) => {
    try {
      const response = await fetch(`${API_BASE}/api/admin/error-logs/${logId}/resolution`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ status })
      });
      
      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          console.log('Error log status updated successfully');
          await fetchErrorLogs();
          await fetchErrorLogsStats();
        }
      }
    } catch (error) {
      console.error('Error updating error log status:', error);
    }
  };

  // Fetch error logs when component mounts or filters change
  useEffect(() => {
    if (token && user && activeTab === 'errorLogs') {
      fetchErrorLogs();
      fetchErrorLogsStats();
    }
  }, [token, user, activeTab, fetchErrorLogs, fetchErrorLogsStats]);

  // Fetch error logs when filters change
  useEffect(() => {
    if (token && user && activeTab === 'errorLogs') {
      fetchErrorLogs();
    }
  }, [token, user, activeTab, errorLogsFilters, fetchErrorLogs]);

  // ========================================
  // USER ACTIVITY FUNCTIONS
  // ========================================

  // Fetch user activities
  const fetchUserActivities = useCallback(async () => {
    try {
      const queryParams = new URLSearchParams({
        userId: userActivityFilters.userId,
        userEmail: userActivityFilters.userEmail,
        userRole: userActivityFilters.userRole,
        page: userActivityFilters.page,
        route: userActivityFilters.route,
        method: userActivityFilters.method,
        action: userActivityFilters.action,
        deviceType: userActivityFilters.deviceType,
        browser: userActivityFilters.browser,
        os: userActivityFilters.os,
        hasError: userActivityFilters.hasError,
        limit: userActivityFilters.limit.toString(),
        page: userActivityFilters.page.toString()
      });

      if (userActivityFilters.startDate) {
        queryParams.append('startDate', userActivityFilters.startDate);
      }
      if (userActivityFilters.endDate) {
        queryParams.append('endDate', userActivityFilters.endDate);
      }

      const response = await fetch(`${API_BASE}/api/admin/user-activities?${queryParams}`, {
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
      });
      
      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          setUserActivities(result.data);
          setUserActivityPagination(result.pagination);
        }
      }
    } catch (error) {
      console.error('Error fetching user activities:', error);
    }
  }, [token, userActivityFilters]);

  // Fetch user activity statistics
  const fetchUserActivityStats = useCallback(async () => {
    try {
      const queryParams = new URLSearchParams();
      if (userActivityFilters.startDate) {
        queryParams.append('startDate', userActivityFilters.startDate);
      }
      if (userActivityFilters.endDate) {
        queryParams.append('endDate', userActivityFilters.endDate);
      }

      const response = await fetch(`${API_BASE}/api/admin/user-activities/stats?${queryParams}`, {
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
      });
      
      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          setUserActivityStats(result.data);
        }
      }
    } catch (error) {
      console.error('Error fetching user activity statistics:', error);
    }
  }, [token, userActivityFilters.startDate, userActivityFilters.endDate]);

  // Fetch frequently visited pages
  const fetchFrequentlyVisitedPages = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE}/api/admin/user-activities/pages?limit=10`, {
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
      });
      
      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          setFrequentlyVisitedPages(result.data);
        }
      }
    } catch (error) {
      console.error('Error fetching frequently visited pages:', error);
    }
  }, [token]);

  // Fetch top active users
  const fetchTopActiveUsers = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE}/api/admin/user-activities/top-users?limit=10`, {
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
      });
      
      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          setTopActiveUsers(result.data);
        }
      }
    } catch (error) {
      console.error('Error fetching top active users:', error);
    }
  }, [token]);

  // Fetch page analytics
  const fetchPageAnalytics = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE}/api/admin/user-activities/page-analytics?limit=20`, {
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
      });
      
      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          setPageAnalytics(result.data);
        }
      }
    } catch (error) {
      console.error('Error fetching page analytics:', error);
    }
  }, [token]);

  // Cleanup old user activity data
  const cleanupUserActivityData = async () => {
    try {
      const response = await fetch(`${API_BASE}/api/admin/user-activities/cleanup`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ daysToKeep: 90 })
      });
      
      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          console.log(`Cleaned up ${result.deletedCount} old user activity records`);
          await fetchUserActivities();
          await fetchUserActivityStats();
        }
      }
    } catch (error) {
      console.error('Error cleaning up user activity data:', error);
    }
  };

  // Test user activity tracking
  const testUserActivityTracking = async () => {
    try {
      const response = await fetch(`${API_BASE}/api/admin/user-activities/test`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ count: 50 })
      });
      
      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          console.log('Test user activity data generated successfully');
          await fetchUserActivities();
          await fetchUserActivityStats();
          await fetchFrequentlyVisitedPages();
          await fetchTopActiveUsers();
          await fetchPageAnalytics();
        }
      }
    } catch (error) {
      console.error('Error testing user activity tracking:', error);
    }
  };

  // Fetch user activities when component mounts or filters change
  useEffect(() => {
    if (token && user && activeTab === 'userActivity') {
      fetchUserActivities();
      fetchUserActivityStats();
      fetchFrequentlyVisitedPages();
      fetchTopActiveUsers();
      fetchPageAnalytics();
    }
  }, [token, user, activeTab, fetchUserActivities, fetchUserActivityStats, fetchFrequentlyVisitedPages, fetchTopActiveUsers, fetchPageAnalytics]);

  // Fetch user activities when filters change
  useEffect(() => {
    if (token && user && activeTab === 'userActivity') {
      fetchUserActivities();
    }
  }, [token, user, activeTab, userActivityFilters, fetchUserActivities]);

  // Clean up old data
  const cleanupOldData = async () => {
    try {
      const response = await fetch(`${API_BASE}/api/admin/monitoring/cleanup`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ daysToKeep: 90 })
      });
      
      if (response.ok) {
        console.log('Old data cleanup completed');
      }
    } catch (error) {
      console.error('Error cleaning up old data:', error);
    }
  };

  // Initialize data collection and fetch historical data
  useEffect(() => {
    if (token && user) {
      fetchCollectionStatus();
      fetchHistoricalData();
    }
  }, [token, user, fetchCollectionStatus, fetchHistoricalData]);

  // Chart configurations
  const getChartOptions = (title, yAxisLabel = 'Value') => ({
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
        labels: {
          color: isDarkMode ? '#ffffff' : '#323130'
        }
      },
      title: {
        display: true,
        text: title,
        color: isDarkMode ? '#ffffff' : '#323130'
      }
    },
    scales: {
      x: {
        ticks: {
          color: isDarkMode ? '#cccccc' : '#605e5c'
        },
        grid: {
          color: isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'
        }
      },
      y: {
        title: {
          display: true,
          text: yAxisLabel,
          color: isDarkMode ? '#ffffff' : '#323130'
        },
        ticks: {
          color: isDarkMode ? '#cccccc' : '#605e5c'
        },
        grid: {
          color: isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'
        }
      }
    }
  });

  // Prepare chart data
  const getPerformanceChartData = () => {
    if (!performanceTrends?.data) return null;

    return {
      labels: performanceTrends.data.p95.map(item => 
        new Date(item.timestamp).toLocaleString('en-US', { 
          month: 'short', 
          day: 'numeric', 
          hour: '2-digit',
          minute: '2-digit'
        })
      ),
      datasets: [
        {
          label: '95th Percentile Response Time (ms)',
          data: performanceTrends.data.p95.map(item => item.value),
          borderColor: '#ff6384',
          backgroundColor: 'rgba(255, 99, 132, 0.1)',
          tension: 0.4,
          fill: true
        },
        {
          label: 'Average Response Time (ms)',
          data: performanceTrends.data.average.map(item => item.value),
          borderColor: '#36a2eb',
          backgroundColor: 'rgba(54, 162, 235, 0.1)',
          tension: 0.4,
          fill: true
        }
      ]
    };
  };

  const getErrorRateChartData = () => {
    if (!errorTrends?.data) return null;

    return {
      labels: errorTrends.data.current.map(item => 
        new Date(item.timestamp).toLocaleString('en-US', { 
          month: 'short', 
          day: 'numeric', 
          hour: '2-digit',
          minute: '2-digit'
        })
      ),
      datasets: [
        {
          label: 'Current Error Rate (%)',
          data: errorTrends.data.current.map(item => item.value),
          borderColor: '#ff6384',
          backgroundColor: 'rgba(255, 99, 132, 0.1)',
          tension: 0.4,
          fill: true
        },
        {
          label: 'Daily Error Rate (%)',
          data: errorTrends.data.daily.map(item => item.value),
          borderColor: '#ffcd56',
          backgroundColor: 'rgba(255, 205, 86, 0.1)',
          tension: 0.4,
          fill: true
        }
      ]
    };
  };

  const getHealthChartData = () => {
    if (!healthTrends?.data) return null;

    return {
      labels: ['Healthy', 'Degraded', 'Error'],
      datasets: [{
        data: [
          healthTrends.data.healthy || 0,
          healthTrends.data.degraded || 0,
          healthTrends.data.error || 0
        ],
        backgroundColor: [
          'rgba(75, 192, 192, 0.8)',
          'rgba(255, 205, 86, 0.8)',
          'rgba(255, 99, 132, 0.8)'
        ],
        borderColor: [
          'rgba(75, 192, 192, 1)',
          'rgba(255, 205, 86, 1)',
          'rgba(255, 99, 132, 1)'
        ],
        borderWidth: 2
      }]
    };
  };

  if (!user || !['admin', 'super'].includes(user.role)) {
    return (
      <div className="monitoring-container">
        <div className="access-denied">
          <FaShieldAlt className="access-denied-icon" />
          <h2>Access Denied</h2>
          <p>You don't have permission to access the monitoring dashboard.</p>
        </div>
      </div>
    );
  }

  const renderOverviewTab = () => (
    <div className="overview-grid">
      {/* System Health Card */}
      <div className="metric-card health-card">
        <div className="card-header">
          <FaExclamationTriangle className="card-icon" />
          <h3>System Health</h3>
        </div>
        <div className="health-status">
          <div className={`status-icon ${overview?.systemHealth?.status === 'healthy' ? 'healthy' : 'degraded'}`}>
            {overview?.systemHealth?.status === 'healthy' ? <MdCheckCircle /> : <MdWarning />}
          </div>
          <div className="status-text">
            <span className={`status ${overview?.systemHealth?.status === 'healthy' ? 'healthy' : 'degraded'}`}>
              {overview?.systemHealth?.status?.toUpperCase() || 'UNKNOWN'}
            </span>
          </div>
        </div>
        <div className="metric-grid">
          <div className="metric">
            <span className="label">Error Rate:</span>
            <span className="value">{overview?.systemHealth?.errorRate || '0.00%'}</span>
          </div>
          <div className="metric">
            <span className="label">Daily Error Rate:</span>
            <span className="value">{overview?.systemHealth?.dailyErrorRate || '0.00%'}</span>
          </div>
          <div className="metric">
            <span className="label">Threshold:</span>
            <span className="value">{overview?.systemHealth?.threshold || '1%'}</span>
          </div>
        </div>
      </div>

      {/* Performance Card */}
      <div className="metric-card perf-card">
        <div className="card-header">
          <MdSpeed className="card-icon" />
          <h3>Performance</h3>
        </div>
        <div className="metric-grid">
          <div className="metric">
            <span className="label">Avg Response:</span>
            <span className="value">{overview?.performance?.avgResponseTime || '0ms'}</span>
          </div>
          <div className="metric">
            <span className="label">95th Percentile:</span>
            <span className="value">{overview?.performance?.p95ResponseTime || '0ms'}</span>
          </div>
          <div className="metric">
            <span className="label">99th Percentile:</span>
            <span className="value">{overview?.performance?.p99ResponseTime || '0ms'}</span>
          </div>
          <div className="metric">
            <span className="label">Target:</span>
            <span className="value">{overview?.performance?.targetResponseTime || '150ms'}</span>
          </div>
          <div className="metric">
            <span className="label">Requests/sec:</span>
            <span className="value">{overview?.performance?.requestsPerSecond || '0'}</span>
          </div>
        </div>
      </div>

      {/* System Info Card */}
      <div className="metric-card system-card">
        <div className="card-header">
          <FaServer className="card-icon" />
          <h3>System Info</h3>
        </div>
        <div className="metric-grid">
          <div className="metric">
            <span className="label">Uptime:</span>
            <span className="value">{formatUptime(overview?.system?.uptime) || '0m'}</span>
          </div>
          <div className="metric">
            <span className="label">Memory:</span>
            <span className="value">{overview?.system?.memoryUsage?.heapUsed || '0'} MB</span>
          </div>
          <div className="metric">
            <span className="label">Node Version:</span>
            <span className="value">{overview?.system?.nodeVersion || 'Unknown'}</span>
          </div>
          <div className="metric">
            <span className="label">Environment:</span>
            <span className="value">{overview?.system?.environment || 'Unknown'}</span>
          </div>
        </div>
      </div>

      {/* WebSocket Card */}
      <div className="metric-card websocket-card">
        <div className="card-header">
          <FaServer className="card-icon" />
          <h3>WebSocket Status</h3>
        </div>
        <div className="metric-grid">
          <div className="metric">
            <span className="label">Active Connections:</span>
            <span className="value">{overview?.websocket?.activeConnections || '0'}</span>
          </div>
          <div className="metric">
            <span className="label">Total Connections:</span>
            <span className="value">{overview?.websocket?.totalConnections || '0'}</span>
          </div>
          <div className="metric">
            <span className="label">Messages/sec:</span>
            <span className="value">{overview?.websocket?.messagesPerSecond || '0'}</span>
          </div>
          <div className="metric">
            <span className="label">Avg Latency:</span>
            <span className="value">{overview?.websocket?.avgLatency || '0'}ms</span>
          </div>
        </div>
      </div>
    </div>
  );

  const renderHistoricalTab = () => (
    <div className="historical-content">
      {/* Data Collection Controls */}
      <div className="collection-controls">
        <div className="control-section">
          <h3>📊 Data Collection</h3>
          <div className="control-buttons">
            <button
              className={`control-btn ${collectionStatus.isCollecting ? 'active' : ''}`}
              onClick={startDataCollection}
              disabled={collectionStatus.isCollecting}
            >
              <FaPlay /> Start Collection
            </button>
            <button
              className="control-btn"
              onClick={stopDataCollection}
              disabled={!collectionStatus.isCollecting}
            >
              <FaStop /> Stop Collection
            </button>
            <button className="control-btn" onClick={collectDataManually}>
              📊 Collect Now
            </button>
            <button className="control-btn warning" onClick={cleanupOldData}>
              <FaTrash /> Cleanup Old Data
            </button>
          </div>
          <div className="collection-status">
            <span className={`status-dot ${collectionStatus.isCollecting ? 'connected' : 'disconnected'}`}></span>
            <span>Status: {collectionStatus.isCollecting ? 'Collecting' : 'Stopped'}</span>
            {collectionStatus.lastCollection && (
              <span>Last: {new Date(collectionStatus.lastCollection).toLocaleString()}</span>
            )}
          </div>
        </div>

        <div className="control-section">
          <h3>⚙️ View Settings</h3>
          <div className="view-controls">
            <div className="control-group">
              <label>Time Range:</label>
              <select value={timeRange} onChange={(e) => setTimeRange(e.target.value)}>
                <option value="1d">Last 24 Hours</option>
                <option value="7d">Last 7 Days</option>
                <option value="30d">Last 30 Days</option>
                <option value="90d">Last 90 Days</option>
              </select>
            </div>
            <div className="control-group">
              <label>Aggregation:</label>
              <select value={aggregationInterval} onChange={(e) => setAggregationInterval(e.target.value)}>
                <option value="minute">Minute</option>
                <option value="hour">Hour</option>
                <option value="day">Day</option>
              </select>
            </div>
            <button className="control-btn" onClick={fetchHistoricalData}>
              🔄 Refresh Data
            </button>
          </div>
        </div>
      </div>

      {/* Charts Grid */}
      <div className="charts-grid">
        {/* Performance Trends Chart */}
        <div className="chart-card">
          <h3>📈 Performance Trends</h3>
          <div className="chart-container">
            {getPerformanceChartData() ? (
              <Line data={getPerformanceChartData()} options={getChartOptions('Response Time Trends', 'Response Time (ms)')} />
            ) : (
              <div className="chart-placeholder">
                <p>No performance data available</p>
                <button onClick={collectDataManually}>Collect Data</button>
              </div>
            )}
          </div>
          {performanceTrends?.data?.summary && (
            <div className="chart-summary">
              <div className="summary-item">
                <span className="label">Avg P95:</span>
                <span className="value">{performanceTrends.data.summary.avgP95}ms</span>
              </div>
              <div className="summary-item">
                <span className="label">Peak P95:</span>
                <span className="value">{performanceTrends.data.summary.peakP95}ms</span>
              </div>
            </div>
          )}
        </div>

        {/* Error Rate Trends Chart */}
        <div className="chart-card">
          <h3>🚨 Error Rate Trends</h3>
          <div className="chart-container">
            {getErrorRateChartData() ? (
              <Line data={getErrorRateChartData()} options={getChartOptions('Error Rate Trends', 'Error Rate (%)')} />
            ) : (
              <div className="chart-placeholder">
                <p>No error data available</p>
                <button onClick={collectDataManually}>Collect Data</button>
              </div>
            )}
          </div>
          {errorTrends?.data?.summary && (
            <div className="chart-summary">
              <div className="summary-item">
                <span className="label">Avg Current:</span>
                <span className="value">{errorTrends.data.summary.avgCurrentRate}%</span>
              </div>
              <div className="summary-item">
                <span className="label">Peak Daily:</span>
                <span className="value">{errorTrends.data.summary.peakDailyRate}%</span>
              </div>
            </div>
          )}
        </div>

        {/* System Health Chart */}
        <div className="chart-card">
          <h3>💚 System Health Overview</h3>
          <div className="chart-container">
            {getHealthChartData() ? (
              <Doughnut 
                data={getHealthChartData()} 
                options={{
                  ...getChartOptions('System Health Status'),
                  plugins: {
                    ...getChartOptions('System Health Status').plugins,
                    legend: {
                      position: 'bottom',
                      labels: { color: isDarkMode ? '#ffffff' : '#323130' }
                    }
                  }
                }} 
              />
            ) : (
              <div className="chart-placeholder">
                <p>No health data available</p>
                <button onClick={collectDataManually}>Collect Data</button>
              </div>
            )}
          </div>
          {healthTrends?.data && (
            <div className="chart-summary">
              <div className="summary-item">
                <span className="label">Total Records:</span>
                <span className="value">{healthTrends.data.total}</span>
              </div>
              <div className="summary-item">
                <span className="label">Healthy Rate:</span>
                <span className="value">
                  {healthTrends.data.total > 0 ? 
                    Math.round((healthTrends.data.healthy / healthTrends.data.total) * 100) : 0}%
                </span>
              </div>
            </div>
          )}
        </div>

        {/* Historical Data Table */}
        <div className="chart-card full-width">
          <h3>📋 Historical Data Records</h3>
          <div className="data-table-container">
            {historicalData?.data && historicalData.data.length > 0 ? (
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Timestamp</th>
                    <th>Status</th>
                    <th>Avg Response (ms)</th>
                    <th>P95 Response (ms)</th>
                    <th>Error Rate (%)</th>
                    <th>Memory (MB)</th>
                    <th>Active Connections</th>
                  </tr>
                </thead>
                <tbody>
                  {historicalData.data.slice(0, 20).map((record, index) => (
                    <tr key={index}>
                      <td>{new Date(record.timestamp).toLocaleString()}</td>
                      <td>
                        <span className={`status-badge status-${record.systemHealth?.status || 'unknown'}`}>
                          {record.systemHealth?.status || 'unknown'}
                        </span>
                      </td>
                      <td>{record.performance?.responseTimes?.average || 'N/A'}</td>
                      <td>{record.performance?.responseTimes?.p95 || 'N/A'}</td>
                      <td>{record.errors?.currentErrorRate || 'N/A'}</td>
                      <td>{record.system?.memoryUsage?.heapUsed || 'N/A'}</td>
                      <td>{record.websocket?.activeConnections || 'N/A'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="chart-placeholder">
                <p>No historical data available</p>
                <button onClick={collectDataManually}>Collect Data</button>
              </div>
            )}
          </div>
          {historicalData?.metadata && (
            <div className="data-summary">
              <span>Total Records: {historicalData.metadata.totalRecords}</span>
              <span>Time Range: {historicalData.metadata.timeRange.start ? 
                `${new Date(historicalData.metadata.timeRange.start).toLocaleDateString()} - ${new Date(historicalData.metadata.timeRange.end).toLocaleDateString()}` : 
                'N/A'}</span>
              <span>Interval: {historicalData.metadata.interval}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  const renderErrorLogsTab = () => (
    <div className="error-logs-content">
      {/* Error Logs Controls */}
      <div className="error-logs-controls">
        <div className="control-section">
          <h3>🔍 Error Logs</h3>
          <div className="control-buttons">
            <button className="control-btn" onClick={fetchErrorLogs}>
              🔄 Refresh Logs
            </button>
            <button className="control-btn" onClick={fetchErrorLogsStats}>
              📊 View Statistics
            </button>
            <button className="control-btn" onClick={fetchErrorLogsTrends}>
              📈 View Trends
            </button>
            <button className="control-btn warning" onClick={cleanupErrorLogs}>
              <FaTrash /> Cleanup Old Logs
            </button>
            <button className="control-btn" onClick={testErrorLogging}>
              🧪 Test Logging
            </button>
          </div>
        </div>

        <div className="control-section">
          <h3>⚙️ Filters</h3>
          <div className="filter-controls">
            <div className="filter-group">
              <label>Level:</label>
              <select 
                value={errorLogsFilters.level} 
                onChange={(e) => setErrorLogsFilters(prev => ({ ...prev, level: e.target.value, page: 1 }))}
              >
                <option value="all">All Levels</option>
                <option value="error">Error</option>
                <option value="warn">Warning</option>
                <option value="info">Info</option>
                <option value="debug">Debug</option>
              </select>
            </div>
            <div className="filter-group">
              <label>Category:</label>
              <select 
                value={errorLogsFilters.category} 
                onChange={(e) => setErrorLogsFilters(prev => ({ ...prev, category: e.target.value, page: 1 }))}
              >
                <option value="all">All Categories</option>
                <option value="authentication">Authentication</option>
                <option value="authorization">Authorization</option>
                <option value="validation">Validation</option>
                <option value="database">Database</option>
                <option value="network">Network</option>
                <option value="file_operation">File Operation</option>
                <option value="third_party_api">Third Party API</option>
                <option value="internal_error">Internal Error</option>
                <option value="performance">Performance</option>
                <option value="security">Security</option>
                <option value="other">Other</option>
              </select>
            </div>
            <div className="filter-group">
              <label>Severity:</label>
              <select 
                value={errorLogsFilters.severity} 
                onChange={(e) => setErrorLogsFilters(prev => ({ ...prev, severity: e.target.value, page: 1 }))}
              >
                <option value="all">All Severities</option>
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
                <option value="critical">Critical</option>
              </select>
            </div>
            <div className="filter-group">
              <label>Status:</label>
              <select 
                value={errorLogsFilters.status} 
                onChange={(e) => setErrorLogsFilters(prev => ({ ...prev, status: e.target.value, page: 1 }))}
              >
                <option value="all">All Statuses</option>
                <option value="open">Open</option>
                <option value="investigating">Investigating</option>
                <option value="resolved">Resolved</option>
                <option value="closed">Closed</option>
              </select>
            </div>
            <div className="filter-group">
              <label>Module:</label>
              <select 
                value={errorLogsFilters.module} 
                onChange={(e) => setErrorLogsFilters(prev => ({ ...prev, module: e.target.value, page: 1 }))}
              >
                <option value="all">All Modules</option>
                <option value="auth">Auth</option>
                <option value="user">User</option>
                <option value="chat">Chat</option>
                <option value="database">Database</option>
                <option value="api">API</option>
                <option value="websocket">WebSocket</option>
                <option value="test">Test</option>
                <option value="unknown">Unknown</option>
              </select>
            </div>
            <div className="filter-group">
              <label>Start Date:</label>
              <input 
                type="datetime-local" 
                value={errorLogsFilters.startDate}
                onChange={(e) => setErrorLogsFilters(prev => ({ ...prev, startDate: e.target.value, page: 1 }))}
              />
            </div>
            <div className="filter-group">
              <label>End Date:</label>
              <input 
                type="datetime-local" 
                value={errorLogsFilters.endDate}
                onChange={(e) => setErrorLogsFilters(prev => ({ ...prev, endDate: e.target.value, page: 1 }))}
              />
            </div>
            <div className="filter-group">
              <label>Limit:</label>
              <select 
                value={errorLogsFilters.limit} 
                onChange={(e) => setErrorLogsFilters(prev => ({ ...prev, limit: parseInt(e.target.value), page: 1 }))}
              >
                <option value={50}>50</option>
                <option value={100}>100</option>
                <option value={200}>200</option>
                <option value={500}>500</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Error Logs Statistics */}
      {errorLogsStats && (
        <div className="error-logs-stats">
          <h3>📊 Error Logs Statistics</h3>
          <div className="stats-grid">
            <div className="stat-card">
              <h4>Total Logs</h4>
              <span className="stat-value">{errorLogsStats.totalErrors}</span>
            </div>
            <div className="stat-card">
              <h4>Total Occurrences</h4>
              <span className="stat-value">{errorLogsStats.totalOccurrences}</span>
            </div>
            <div className="stat-card">
              <h4>Avg Resolution Time</h4>
              <span className="stat-value">{errorLogsStats.avgResolutionTime}ms</span>
            </div>
            <div className="stat-card">
              <h4>By Level</h4>
              <div className="stat-breakdown">
                {Object.entries(errorLogsStats.errorsByLevel || {}).map(([level, count]) => (
                  <div key={level} className="breakdown-item">
                    <span className="label">{level}:</span>
                    <span className="value">{count}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="stat-card">
              <h4>By Category</h4>
              <div className="stat-breakdown">
                {Object.entries(errorLogsStats.errorsByCategory || {}).map(([category, count]) => (
                  <div key={category} className="breakdown-item">
                    <span className="label">{category}:</span>
                    <span className="value">{count}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="stat-card">
              <h4>By Severity</h4>
              <div className="stat-breakdown">
                {Object.entries(errorLogsStats.errorsBySeverity || {}).map(([severity, count]) => (
                  <div key={severity} className="breakdown-item">
                    <span className="label">{severity}:</span>
                    <span className="value">{count}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Error Logs Table */}
      <div className="error-logs-table">
        <h3>📋 Error Logs</h3>
        {errorLogs.length > 0 ? (
          <div className="table-container">
            <table className="error-logs-table-content">
              <thead>
                <tr>
                  <th>Timestamp</th>
                  <th>Level</th>
                  <th>Message</th>
                  <th>Category</th>
                  <th>Severity</th>
                  <th>Module</th>
                  <th>Status</th>
                  <th>Occurrences</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {errorLogs.map((log) => (
                  <tr key={log._id} className={`error-log-row error-log-${log.level}`}>
                    <td className="timestamp">
                      {new Date(log.timestamp).toLocaleString()}
                    </td>
                    <td>
                      <span className={`log-level log-${log.level}`}>
                        {log.level.toUpperCase()}
                      </span>
                    </td>
                    <td className="message">
                      <div className="message-content">
                        <div className="message-text">{log.message}</div>
                        {log.errorType && (
                          <div className="error-type">{log.errorType}</div>
                        )}
                      </div>
                    </td>
                    <td>
                      <span className={`category-badge category-${log.category}`}>
                        {log.category}
                      </span>
                    </td>
                    <td>
                      <span className={`severity-badge severity-${log.severity}`}>
                        {log.severity}
                      </span>
                    </td>
                    <td>{log.context?.module || 'unknown'}</td>
                    <td>
                      <span className={`status-badge status-${log.resolution?.status || 'open'}`}>
                        {log.resolution?.status || 'open'}
                      </span>
                    </td>
                    <td>
                      <div className="occurrences-info">
                        <span className="count">{log.occurrences?.count || 1}</span>
                        {log.occurrences?.frequency > 0 && (
                          <span className="frequency">({log.occurrences.frequency.toFixed(2)}/hr)</span>
                        )}
                      </div>
                    </td>
                    <td>
                      <div className="action-buttons">
                        <button 
                          className="action-btn view"
                          onClick={() => viewErrorLogDetails(log)}
                          title="View Details"
                        >
                          👁️
                        </button>
                        <button 
                          className="action-btn edit"
                          onClick={() => editErrorLog(log)}
                          title="Edit"
                        >
                          ✏️
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            
            {/* Pagination */}
            {errorLogsPagination.pages > 1 && (
              <div className="pagination">
                                 <button 
                   className="pagination-btn"
                   onClick={() => setErrorLogsFilters(prev => ({ ...prev, page: prev.page - 1 }))}
                   disabled={errorLogsPagination.page <= 1}
                 >
                   ← Previous
                 </button>
                <span className="pagination-info">
                  Page {errorLogsPagination.page} of {errorLogsPagination.pages}
                </span>
                <button 
                  className="pagination-btn"
                  onClick={() => setErrorLogsFilters(prev => ({ ...prev, page: prev.page + 1 }))}
                  disabled={errorLogsPagination.page >= errorLogsPagination.pages}
                >
                  Next →
                </button>
              </div>
            )}
          </div>
        ) : (
          <div className="no-data">
            <p>No error logs found with current filters</p>
            <button onClick={fetchErrorLogs}>Refresh</button>
          </div>
        )}
      </div>
    </div>
  );

  const renderUserActivityTab = () => (
    <div className="user-activity-content">
      {/* User Activity Controls */}
      <div className="user-activity-controls">
        <div className="control-section">
          <h3>👥 User Activity Tracking</h3>
          <div className="control-buttons">
            <button className="control-btn" onClick={fetchUserActivities}>
              🔄 Refresh Activities
            </button>
            <button className="control-btn" onClick={fetchUserActivityStats}>
              📊 View Statistics
            </button>
            <button className="control-btn" onClick={fetchFrequentlyVisitedPages}>
              📈 View Popular Pages
            </button>
            <button className="control-btn" onClick={fetchTopActiveUsers}>
              👑 Top Users
            </button>
            <button className="control-btn" onClick={fetchPageAnalytics}>
              📊 Page Analytics
            </button>
            <button className="control-btn warning" onClick={cleanupUserActivityData}>
              <FaTrash /> Cleanup Old Data
            </button>
            <button className="control-btn" onClick={testUserActivityTracking}>
              🧪 Test Tracking
            </button>
          </div>
        </div>

        <div className="control-section">
          <h3>⚙️ Filters</h3>
          <div className="filter-controls">
            <div className="filter-group">
              <label>User ID:</label>
              <input 
                type="text" 
                value={userActivityFilters.userId}
                onChange={(e) => setUserActivityFilters(prev => ({ ...prev, userId: e.target.value, page: 1 }))}
                placeholder="Filter by user ID"
              />
            </div>
            <div className="filter-group">
              <label>User Email:</label>
              <input 
                type="text" 
                value={userActivityFilters.userEmail}
                onChange={(e) => setUserActivityFilters(prev => ({ ...prev, userEmail: e.target.value, page: 1 }))}
                placeholder="Filter by email"
              />
            </div>
            <div className="filter-group">
              <label>User Role:</label>
              <select 
                value={userActivityFilters.userRole} 
                onChange={(e) => setUserActivityFilters(prev => ({ ...prev, userRole: e.target.value, page: 1 }))}
              >
                <option value="all">All Roles</option>
                <option value="admin">Admin</option>
                <option value="super">Super</option>
                <option value="user">User</option>
              </select>
            </div>
            <div className="filter-group">
              <label>Page:</label>
              <input 
                type="text" 
                value={userActivityFilters.page}
                onChange={(e) => setUserActivityFilters(prev => ({ ...prev, page: e.target.value, page: 1 }))}
                placeholder="Filter by page name"
              />
            </div>
            <div className="filter-group">
              <label>Route:</label>
              <input 
                type="text" 
                value={userActivityFilters.route}
                onChange={(e) => setUserActivityFilters(prev => ({ ...prev, route: e.target.value, page: 1 }))}
                placeholder="Filter by route"
              />
            </div>
            <div className="filter-group">
              <label>Method:</label>
              <select 
                value={userActivityFilters.method} 
                onChange={(e) => setUserActivityFilters(prev => ({ ...prev, method: e.target.value, page: 1 }))}
              >
                <option value="all">All Methods</option>
                <option value="GET">GET</option>
                <option value="POST">POST</option>
                <option value="PUT">PUT</option>
                <option value="DELETE">DELETE</option>
              </select>
            </div>
            <div className="filter-group">
              <label>Action:</label>
              <select 
                value={userActivityFilters.action} 
                onChange={(e) => setUserActivityFilters(prev => ({ ...prev, action: e.target.value, page: 1 }))}
              >
                <option value="all">All Actions</option>
                <option value="view">View</option>
                <option value="create">Create</option>
                <option value="update">Update</option>
                <option value="delete">Delete</option>
                <option value="search">Search</option>
              </select>
            </div>
            <div className="filter-group">
              <label>Device Type:</label>
              <select 
                value={userActivityFilters.deviceType} 
                onChange={(e) => setUserActivityFilters(prev => ({ ...prev, deviceType: e.target.value, page: 1 }))}
              >
                <option value="all">All Devices</option>
                <option value="desktop">Desktop</option>
                <option value="mobile">Mobile</option>
                <option value="tablet">Tablet</option>
              </select>
            </div>
            <div className="filter-group">
              <label>Browser:</label>
              <select 
                value={userActivityFilters.browser} 
                onChange={(e) => setUserActivityFilters(prev => ({ ...prev, browser: e.target.value, page: 1 }))}
              >
                <option value="all">All Browsers</option>
                <option value="Chrome">Chrome</option>
                <option value="Firefox">Firefox</option>
                <option value="Safari">Safari</option>
                <option value="Edge">Edge</option>
                <option value="Other">Other</option>
              </select>
            </div>
            <div className="filter-group">
              <label>OS:</label>
              <select 
                value={userActivityFilters.os} 
                onChange={(e) => setUserActivityFilters(prev => ({ ...prev, os: e.target.value, page: 1 }))}
              >
                <option value="all">All OS</option>
                <option value="Windows">Windows</option>
                <option value="macOS">macOS</option>
                <option value="Linux">Linux</option>
                <option value="Android">Android</option>
                <option value="iOS">iOS</option>
                <option value="Other">Other</option>
              </select>
            </div>
            <div className="filter-group">
              <label>Has Error:</label>
              <select 
                value={userActivityFilters.hasError} 
                onChange={(e) => setUserActivityFilters(prev => ({ ...prev, hasError: e.target.value, page: 1 }))}
              >
                <option value="all">All</option>
                <option value="true">Yes</option>
                <option value="false">No</option>
              </select>
            </div>
            <div className="filter-group">
              <label>Start Date:</label>
              <input 
                type="datetime-local" 
                value={userActivityFilters.startDate}
                onChange={(e) => setUserActivityFilters(prev => ({ ...prev, startDate: e.target.value, page: 1 }))}
              />
            </div>
            <div className="filter-group">
              <label>End Date:</label>
              <input 
                type="datetime-local" 
                value={userActivityFilters.endDate}
                onChange={(e) => setUserActivityFilters(prev => ({ ...prev, endDate: e.target.value, page: 1 }))}
              />
            </div>
            <div className="filter-group">
              <label>Limit:</label>
              <select 
                value={userActivityFilters.limit} 
                onChange={(e) => setUserActivityFilters(prev => ({ ...prev, limit: parseInt(e.target.value), page: 1 }))}
              >
                <option value={50}>50</option>
                <option value={100}>100</option>
                <option value={200}>200</option>
                <option value={500}>500</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* User Activity Statistics */}
      {userActivityStats && (
        <div className="user-activity-stats">
          <h3>📊 User Activity Statistics</h3>
          <div className="stats-grid">
            <div className="stat-card">
              <h4>Total Activities</h4>
              <span className="stat-value">{userActivityStats.totalActivities}</span>
            </div>
            <div className="stat-card">
              <h4>Unique Users</h4>
              <span className="stat-value">{userActivityStats.uniqueUsers}</span>
            </div>
            <div className="stat-card">
              <h4>Unique Pages</h4>
              <span className="stat-value">{userActivityStats.uniquePages}</span>
            </div>
            <div className="stat-card">
              <h4>Unique Routes</h4>
              <span className="stat-value">{userActivityStats.uniqueRoutes}</span>
            </div>
            <div className="stat-card">
              <h4>Avg Duration</h4>
              <span className="stat-value">{userActivityStats.avgDuration}ms</span>
            </div>
            <div className="stat-card">
              <h4>Avg Load Time</h4>
              <span className="stat-value">{userActivityStats.avgLoadTime}ms</span>
            </div>
            <div className="stat-card">
              <h4>Avg Response Time</h4>
              <span className="stat-value">{userActivityStats.avgResponseTime}ms</span>
            </div>
            <div className="stat-card">
              <h4>Total Errors</h4>
              <span className="stat-value error">{userActivityStats.totalErrors}</span>
            </div>
            <div className="stat-card">
              <h4>Error Rate</h4>
              <span className="stat-value">{userActivityStats.errorRate}%</span>
            </div>
            <div className="stat-card">
              <h4>Total Clicks</h4>
              <span className="stat-value">{userActivityStats.totalClicks}</span>
            </div>
            <div className="stat-card">
              <h4>Avg Time on Page</h4>
              <span className="stat-value">{userActivityStats.avgTimeOnPage}ms</span>
            </div>
          </div>
        </div>
      )}

      {/* Frequently Visited Pages */}
      {frequentlyVisitedPages.length > 0 && (
        <div className="frequently-visited-pages">
          <h3>📈 Frequently Visited Pages</h3>
          <div className="pages-grid">
            {frequentlyVisitedPages.map((page, index) => (
              <div key={index} className="page-card">
                <div className="page-header">
                  <h4>{page.page}</h4>
                  <span className="route">{page.route}</span>
                </div>
                <div className="page-stats">
                  <div className="stat">
                    <span className="label">Visits:</span>
                    <span className="value">{page.visitCount}</span>
                  </div>
                  <div className="stat">
                    <span className="label">Unique Users:</span>
                    <span className="value">{page.uniqueUsers}</span>
                  </div>
                  <div className="stat">
                    <span className="label">Avg Duration:</span>
                    <span className="value">{page.avgDuration}ms</span>
                  </div>
                  <div className="stat">
                    <span className="label">Avg Load Time:</span>
                    <span className="value">{page.avgLoadTime}ms</span>
                  </div>
                  <div className="stat">
                    <span className="label">Error Rate:</span>
                    <span className="value">{page.errorRate}%</span>
                  </div>
                  <div className="stat">
                    <span className="label">Last Visited:</span>
                    <span className="value">{new Date(page.lastVisited).toLocaleString()}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Top Active Users */}
      {topActiveUsers.length > 0 && (
        <div className="top-active-users">
          <h3>👑 Top Active Users</h3>
          <div className="users-grid">
            {topActiveUsers.map((user, index) => (
              <div key={index} className="user-card">
                <div className="user-header">
                  <h4>{user.userEmail}</h4>
                  <span className={`role-badge role-${user.userRole}`}>{user.userRole}</span>
                </div>
                <div className="user-stats">
                  <div className="stat">
                    <span className="label">Activities:</span>
                    <span className="value">{user.activityCount}</span>
                  </div>
                  <div className="stat">
                    <span className="label">Unique Pages:</span>
                    <span className="value">{user.uniquePages}</span>
                  </div>
                  <div className="stat">
                    <span className="label">Total Duration:</span>
                    <span className="value">{Math.round(user.totalDuration / 1000)}s</span>
                  </div>
                  <div className="stat">
                    <span className="label">Avg Duration:</span>
                    <span className="value">{user.avgDuration}ms</span>
                  </div>
                  <div className="stat">
                    <span className="label">Total Clicks:</span>
                    <span className="value">{user.totalClicks}</span>
                  </div>
                  <div className="stat">
                    <span className="label">Error Rate:</span>
                    <span className="value">{user.errorRate}%</span>
                  </div>
                  <div className="stat">
                    <span className="label">Last Activity:</span>
                    <span className="value">{new Date(user.lastActivity).toLocaleString()}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* User Activities Table */}
      <div className="user-activities-table">
        <h3>📋 User Activities</h3>
        {userActivities.length > 0 ? (
          <div className="table-container">
            <table className="user-activities-table-content">
              <thead>
                <tr>
                  <th>Timestamp</th>
                  <th>User</th>
                  <th>Page</th>
                  <th>Route</th>
                  <th>Method</th>
                  <th>Action</th>
                  <th>Device</th>
                  <th>Browser</th>
                  <th>OS</th>
                  <th>Duration</th>
                  <th>Load Time</th>
                  <th>Status</th>
                  <th>Details</th>
                </tr>
              </thead>
              <tbody>
                {userActivities.map((activity) => (
                  <tr key={activity._id} className={`activity-row activity-${activity.errorOccurred ? 'error' : 'normal'}`}>
                    <td className="timestamp">
                      {new Date(activity.timestamp).toLocaleString()}
                    </td>
                    <td>
                      <div className="user-info">
                        <div className="user-email">{activity.userEmail}</div>
                        <div className="user-role">{activity.userRole}</div>
                      </div>
                    </td>
                    <td>{activity.page}</td>
                    <td className="route">{activity.route}</td>
                    <td>
                      <span className={`method-badge method-${activity.method}`}>
                        {activity.method}
                      </span>
                    </td>
                    <td>{activity.action || 'N/A'}</td>
                    <td>
                      <span className={`device-badge device-${activity.deviceType}`}>
                        {activity.deviceType}
                      </span>
                    </td>
                    <td>{activity.browser}</td>
                    <td>{activity.os}</td>
                    <td>{Math.round(activity.duration / 1000)}s</td>
                    <td>{activity.loadTime}ms</td>
                    <td>
                      <span className={`status-badge status-${activity.statusCode >= 400 ? 'error' : 'success'}`}>
                        {activity.statusCode}
                      </span>
                    </td>
                    <td>
                      <div className="activity-details">
                        {activity.errorOccurred && (
                          <div className="error-indicator">⚠️</div>
                        )}
                        {activity.clicks > 0 && (
                          <div className="clicks-info">🖱️ {activity.clicks}</div>
                        )}
                        {activity.scrollDepth > 0 && (
                          <div className="scroll-info">📜 {activity.scrollDepth}%</div>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            
            {/* Pagination */}
            {userActivityPagination.pages > 1 && (
              <div className="pagination">
                <button 
                  className="pagination-btn"
                  onClick={() => setUserActivityFilters(prev => ({ ...prev, page: prev.page - 1 }))}
                  disabled={userActivityPagination.page <= 1}
                >
                  ← Previous
                </button>
                <span className="pagination-info">
                  Page {userActivityPagination.page} of {userActivityPagination.pages}
                </span>
                <button 
                  className="pagination-btn"
                  onClick={() => setUserActivityFilters(prev => ({ ...prev, page: prev.page + 1 }))}
                  disabled={userActivityPagination.page >= userActivityPagination.pages}
                >
                  Next →
                </button>
              </div>
            )}
          </div>
        ) : (
          <div className="no-data">
            <p>No user activities found with current filters</p>
            <button onClick={fetchUserActivities}>Refresh</button>
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div className="monitoring-container">
      <div className="monitoring-header">
        <div className="header-left">
          <FaServer className="header-icon" />
          <div>
            <h1>System Monitoring Dashboard</h1>
            <p>Real-time system health and performance metrics with historical data</p>
          </div>
        </div>
        <div className="header-right">
          <div className="connection-status">
            <span className={`status-dot ${connectionStatus}`}></span>
            <span className="status-text">
              {connectionStatus === 'connected' ? 'Live Updates' : 
               connectionStatus === 'error' ? 'Connection Error' : 'Disconnected'}
            </span>
          </div>
          <button
            className="theme-toggle-btn"
            onClick={toggleTheme}
            title={isDarkMode ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
          >
            {isDarkMode ? <FaSun /> : <FaMoon />}
          </button>
          <button
            className="refresh-btn"
            onClick={fetchMonitoringData}
            disabled={loading}
          >
            {loading ? 'Refreshing...' : 'Refresh Data'}
          </button>
          <div className="export-buttons">
            <button
              className="export-btn"
              onClick={() => exportData('json')}
              title="Export as JSON"
            >
              📊 JSON
            </button>
            <button
              className="export-btn"
              onClick={() => exportData('csv')}
              title="Export as CSV"
            >
              📊 CSV
            </button>
          </div>
          {lastUpdated && (
            <p className="last-updated">Last updated: {lastUpdated.toLocaleTimeString()}</p>
          )}
        </div>
      </div>

      {/* Alerts Section */}
      {alerts.length > 0 && (
        <div className="alerts-section">
          <div className="alerts-header">
            <h3>🚨 Active Alerts ({alerts.length})</h3>
          </div>
          <div className="alerts-list">
            {alerts.map((alert) => (
              <div key={alert.id} className={`alert-item alert-${alert.type}`}>
                <div className="alert-icon">
                  {alert.type === 'critical' ? <MdError /> : <MdWarning />}
                </div>
                <div className="alert-content">
                  <h4>{alert.title}</h4>
                  <p>{alert.message}</p>
                  <span className="alert-time">
                    {new Date(alert.timestamp).toLocaleTimeString()}
                  </span>
                </div>
                <button 
                  className="alert-dismiss"
                  onClick={() => setAlerts(alerts.filter(a => a.id !== alert.id))}
                >
                  ×
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="monitoring-tabs">
        <button
          className={`tab-btn ${activeTab === 'overview' ? 'active' : ''}`}
          onClick={() => setActiveTab('overview')}
        >
          Overview
        </button>
        <button
          className={`tab-btn ${activeTab === 'errors' ? 'active' : ''}`}
          onClick={() => setActiveTab('errors')}
        >
          Errors
        </button>
        <button
          className={`tab-btn ${activeTab === 'performance' ? 'active' : ''}`}
          onClick={() => setActiveTab('performance')}
        >
          Performance
        </button>

        <button
          className={`tab-btn ${activeTab === 'rate-limits' ? 'active' : ''}`}
          onClick={() => setActiveTab('rate-limits')}
        >
          Rate Limits
        </button>
        <button
          className={`tab-btn ${activeTab === 'historical' ? 'active' : ''}`}
          onClick={() => setActiveTab('historical')}
        >
          <MdHistory /> Historical Data
        </button>
        <button
          className={`tab-btn ${activeTab === 'error-logs' ? 'active' : ''}`}
          onClick={() => setActiveTab('error-logs')}
        >
          <MdError /> Error Logs
        </button>
        <button
          className={`tab-btn ${activeTab === 'userActivity' ? 'active' : ''}`}
          onClick={() => setActiveTab('userActivity')}
        >
          👥 User Activity
        </button>
      </div>

      <div className="monitoring-content">
        {loading ? (
          <div className="loading-spinner">
            <div className="spinner"></div>
            <p>Loading monitoring data...</p>
          </div>
        ) : (
          <>
            {/* Overview Tab */}
            {activeTab === 'overview' && overview && renderOverviewTab()}

            {/* Errors Tab */}
            {activeTab === 'errors' && errors && (
              <div className="errors-content">
                <div className="error-stats">
                  <div className="stat-card">
                    <h3>Total Requests</h3>
                    <span className="stat-value">{errors.totalRequests}</span>
                  </div>
                  <div className="stat-card">
                    <h3>Total Errors</h3>
                    <span className="stat-value error">{errors.totalErrors}</span>
                  </div>
                  <div className="stat-card">
                    <h3>Current Error Rate</h3>
                    <span className="stat-value">{errors.currentErrorRate}</span>
                  </div>
                  <div className="stat-card">
                    <h3>Daily Error Rate</h3>
                    <span className="stat-value">{errors.dailyErrorRate}</span>
                  </div>
                </div>
                
                {errors.recommendations && errors.recommendations.length > 0 && (
                  <div className="recommendations">
                    <h3>Recommendations</h3>
                    <ul>
                      {errors.recommendations.map((rec, index) => (
                        <li key={index} className="recommendation-item">
                          {rec}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}

            {/* Performance Tab */}
            {activeTab === 'performance' && performance && (
              <div className="performance-content">
                <div className="perf-grid">
                  <div className="perf-card">
                    <h3>Response Times</h3>
                    <div className="metric-list">
                      <div className="metric">
                        <span className="label">Average:</span>
                        <span className="value">{performance.responseTimes.average}ms</span>
                      </div>
                      <div className="metric">
                        <span className="label">95th Percentile:</span>
                        <span className="value">{performance.responseTimes.p95}ms</span>
                      </div>
                      <div className="metric">
                        <span className="label">99th Percentile:</span>
                        <span className="value">{performance.responseTimes.p99}ms</span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="perf-card">
                    <h3>Throughput</h3>
                    <div className="metric-list">
                      <div className="metric">
                        <span className="label">Requests/sec:</span>
                        <span className="value">{performance.throughput.requestsPerSecond}</span>
                      </div>
                      <div className="metric">
                        <span className="label">Total Requests:</span>
                        <span className="value">{performance.throughput.totalRequests}</span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="perf-card">
                    <h3>Database</h3>
                    <div className="metric-list">
                      <div className="metric">
                        <span className="label">Avg Query Time:</span>
                        <span className="value">{performance.database.avgQueryTime}ms</span>
                      </div>
                      <div className="metric">
                        <span className="label">Slow Queries:</span>
                        <span className="value">{performance.database.slowQueries}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}



            {/* Rate Limits Tab */}
            {activeTab === 'rate-limits' && rateLimits && (
              <div className="rate-limits-content">
                <div className="rate-limits-grid">
                  <div className="rate-limit-card">
                    <h3>HTTP Rate Limits</h3>
                    <div className="metric-list">
                      <div className="metric">
                        <span className="label">Per User - Total:</span>
                        <span className="value">{rateLimits.http?.perUser?.total || 0}</span>
                      </div>
                      <div className="metric">
                        <span className="label">Per User - Blocked:</span>
                        <span className="value error">{rateLimits.http?.perUser?.blocked || 0}</span>
                      </div>
                      <div className="metric">
                        <span className="label">Per IP - Total:</span>
                        <span className="value">{rateLimits.http?.perIP?.total || 0}</span>
                      </div>
                      <div className="metric">
                        <span className="label">Per IP - Blocked:</span>
                        <span className="value error">{rateLimits.http?.perIP?.blocked || 0}</span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="rate-limit-card">
                    <h3>WebSocket Rate Limits</h3>
                    <div className="metric-list">
                      <div className="metric">
                        <span className="label">Per User - Total:</span>
                        <span className="value">{rateLimits.websocket?.perUser?.total || 0}</span>
                      </div>
                      <div className="metric">
                        <span className="label">Per User - Blocked:</span>
                        <span className="value error">{rateLimits.websocket?.perUser?.blocked || 0}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Historical Data Tab */}
            {activeTab === 'historical' && renderHistoricalTab()}

            {/* Error Logs Tab */}
            {activeTab === 'error-logs' && renderErrorLogsTab()}

            {/* User Activity Tab */}
            {activeTab === 'userActivity' && renderUserActivityTab()}
          </>
        )}
      </div>
    </div>
  );
}
